import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CurveCalculationResult, Filter, FilterValues, MesRequest, Tags } from '../models';
import { INITIAL_FILTER, INITIAL_FILTER_VALUES, INITIAL_MES_REQUEST } from '../constants';
import { VsdOperationType } from '../enums';
import { ClientMotorResponse } from '../models/ClientMotorResponse';

export interface MotorState {
    loading: boolean;
    filter: Filter;
    filterValues: FilterValues;
    mesRequest: MesRequest;
    showNumberOfStartupsDialog: boolean;
    hasMotor: boolean;
    calculationSuccessful: boolean;
    updateFilters: boolean;
    tags?: Tags;
    calculationResponse?: CurveCalculationResult;
    clientMotorResponse?: ClientMotorResponse;
    vsdOperationType: VsdOperationType;
    renderLineOperations: boolean,
    renderConverterOperation: boolean,
    disableOperationalData: boolean,
    motorSelectionAccordionOpen: boolean
}

const initialState: MotorState = {
    loading: false,
    calculationSuccessful: true,
    showNumberOfStartupsDialog: false,
    hasMotor: false,
    filter: INITIAL_FILTER,
    filterValues: INITIAL_FILTER_VALUES,
    mesRequest: INITIAL_MES_REQUEST,
    updateFilters: false,
    vsdOperationType: VsdOperationType.ConverterOperationVSD,
    renderLineOperations: false,
    renderConverterOperation: false,
    disableOperationalData: false,
    motorSelectionAccordionOpen: true
};

export const motorSlice = createSlice({
    name: 'motor',
    initialState,
    reducers: {
        setFilterValues: (state: MotorState, action: PayloadAction<FilterValues>) => {
            state.filterValues = action.payload;
        },
        setFilter: (state: MotorState, action: PayloadAction<Partial<Filter>>) => {
            state.filter = { ...state.filter, ...action.payload };
        },
        resetFilter: (state: MotorState, action: PayloadAction<Filter>) => {
            state.filter = action.payload;
        },
        setTags: (state: MotorState, action: PayloadAction<Tags>) => {
            state.tags = { ...state.tags, ...action.payload };
        },
        removeTags: (state: MotorState) => {
            state.tags = undefined;
        },
        setMesRequest: (state: MotorState, action: PayloadAction<MesRequest>) => {
            state.mesRequest = action.payload;
        },
        setCalculationResponse: (state: MotorState, action: PayloadAction<CurveCalculationResult | undefined>) => {
            state.calculationResponse = action.payload;
        },        
        setCalculationSuccessful: (state: MotorState, action: PayloadAction<boolean>) => {
            state.calculationSuccessful = action.payload;
        },
        setHasMotor: (state: MotorState, action: PayloadAction<boolean>) => {
            state.hasMotor = action.payload;
        },
        setNumberOfStartupsDialog: (state: MotorState, action: PayloadAction<boolean>) => {
            state.showNumberOfStartupsDialog = action.payload;
        },
        setClientMotorResponse: (state: MotorState, action: PayloadAction<ClientMotorResponse>) => {
            state.clientMotorResponse = action.payload;
        },
        setVsdOperationType: (state: MotorState, action: PayloadAction<VsdOperationType>) => {
            state.vsdOperationType = action.payload;
        },
        toggleUpdateFilters: (state: MotorState) => {
            state.updateFilters = !state.updateFilters;
        },
        toggleLoading: (state: MotorState) => {
            state.loading = !state.loading;
        },
        setRenderLineOperations: (state: MotorState, action: PayloadAction<boolean>) => {
            state.renderLineOperations = action.payload;
        },
        setRenderConverterOperation: (state: MotorState, action: PayloadAction<boolean>) => {
            state.renderConverterOperation = action.payload;
        },
        setDisableOperationalData: (state: MotorState, action: PayloadAction<boolean>) => {
            state.disableOperationalData = action.payload;
        },
        toggleMotorSelectionAccordion: (state: MotorState, action: PayloadAction<boolean>) => {
            state.motorSelectionAccordionOpen = action.payload;
        }
    }
});

export const {
    setFilterValues,
    setFilter,
    resetFilter,
    setMesRequest,
    setCalculationResponse,
    setNumberOfStartupsDialog,
    setCalculationSuccessful,
    setHasMotor,
    setTags,
    removeTags,
    setClientMotorResponse,
    setVsdOperationType,
    toggleLoading,
    toggleUpdateFilters,
    setRenderLineOperations,
    setRenderConverterOperation,
    setDisableOperationalData,
    toggleMotorSelectionAccordion
} = motorSlice.actions;
export const motorReducer = motorSlice.reducer;
